import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import Panel from '../Panel';
import Break from '../Break';
import { BlueBorderButton, BlueButton } from 'components/Button';
import Text from 'components/Text';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowDisplay,
  PanelButtonRow,
  PanelRowTextForm,
  FullPanel,
  PanelContent,
  StyledForm,
  MultipleInputRowContainer,
  InputAndLabelContainer,
  LabledInput,
  InputUnitLabel
} from 'components/PanelComponents';
import SelectableSearchInput from 'components/SelectableSearchInput';
import {
  LOCAL_RESOURCE_MAPPED_VALUES,
  NO_PARENT_OPTION_VALUE,
  RESOURCE_TYPES,
  SIDEPANEL_STATES
} from 'utils/appConstants';
import {
  createToponode,
  duplicateTopoNode
} from '../../reducers/toponode/actions';
import { createIndeNode, duplicateIndeNode } from 'reducers/indenode/actions';
import {
  closeSidePanel,
  revertSidePanel,
  setSidePanel
} from '../../reducers/sidePanel/actions';
import { determineBack } from 'utils/determineBack';
import { fetchAllTopoNodes } from 'api/toponode';
import { convertToSeconds } from 'utils/format';
import {
  POLL_INDENODES,
  POLL_TOPONODES,
  subscribe,
  unsubscribe
} from 'reducers/polling/actions';

const PRIMACY_MAX_VALUE = parseInt(window.env.REACT_APP_PRIMACY_MAX_VALUE);
const PRIMACY_MIN_VALUE = parseInt(window.env.REACT_APP_PRIMACY_MIN_VALUE);

const SUBSCRIBER_NAME = 'NewNode';
const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);

const NewNode = ({ isActive }) => {
  const dispatch = useDispatch();
  const topoNodes = useSelector(state => state.topoNode.topoNodes);
  const indeNodes = useSelector(state => state.indeNode.indeNodes);
  const sidePanel = useSelector(state => state.sidePanel);
  const data = useSelector(state => state.sidePanel.data);
  const [id, setId] = useState('');
  const [primacy, setPrimacy] = useState('');
  const [description, setDescription] = useState('');
  const [parentTopoNodeId, setParentTopoNodeId] = useState(null);
  const [descriptionIsError, setDescriptionIsError] = useState(false);
  const [idIsError, setIdIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [childsParentTopoNodeId, setChildsParentTopoNodeId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newTopoNode, setNewTopoNode] = useState([]);
  const [primacyIsError, setPrimacyIsError] = useState(false);
  const [derProgramListPollRateIsError, setDerProgramListPollRateIsError] =
    useState(false);
  const [derProgramListPollRateHours, setDerProgramListPollRateHours] =
    useState(null);
  const [derProgramListPollRateMinutes, setDerProgramListPollRateMinutes] =
    useState(null);
  const [derProgramListPollRateSeconds, setDerProgramListPollRateSeconds] =
    useState(null);

  useEffect(() => {
    if (data && data.parentTopoNodeId) {
      const { parentTopoNodeId } = data;
      setChildsParentTopoNodeId(parentTopoNodeId);
    }
    const findTopoNodes = async () => {
      const fetchTopoNodes = await fetchAllTopoNodes();
      setIsLoading(false);
      setNewTopoNode(fetchTopoNodes);
    };
    findTopoNodes();
  }, []);

  useEffect(() => {
    if (isActive) {
      dispatch(
        subscribe({
          subscriber: SUBSCRIBER_NAME,
          pollTypes: [
            {
              type: POLL_TOPONODES
            },
            {
              type: POLL_INDENODES
            }
          ],
          pollRate
        })
      );
    } else {
      dispatch(unsubscribe(SUBSCRIBER_NAME));
    }
    return () => {
      if (isActive) {
        dispatch(unsubscribe(SUBSCRIBER_NAME));
      }
    };
  }, [dispatch, isActive]);

  const handleInputChange = event => {
    const target = event.target;
    const value = target.value === NO_PARENT_OPTION_VALUE ? null : target.value;
    const name = target.name;
    if (name === 'id') {
      setId(value);
    } else if (name === 'description') {
      setDescription(value);
    } else if (name === 'primacy') {
      setPrimacy(value);
    } else if (name === 'derProgramListPollRateHours') {
      setDerProgramListPollRateHours(value);
    } else if (name === 'derProgramListPollRateMinutes') {
      setDerProgramListPollRateMinutes(value);
    } else if (name === 'derProgramListPollRateSeconds') {
      setDerProgramListPollRateSeconds(value);
    }
  };

  // Function for node search
  const handleSearchInputChange = (selectedOptions, input) => {
    if (Array.isArray(selectedOptions)) {
      if (input === 'parentTopoNodeId') {
        setParentTopoNodeId(selectedOptions.map(option => option.value));
      }
      return;
    }
    if (input === 'parentTopoNodeId') {
      setParentTopoNodeId(get(selectedOptions, 'value', ''));
    }
  };

  const handleSubmit = async e => {
    const { entity } = sidePanel;
    e.preventDefault();
    setIsSubmitting(true);

    let nodeIds = [];
    topoNodes.forEach(x =>
      nodeIds.push(x.hasOwnProperty('topoNodeId') ? x.topoNodeId : x.indeNodeId)
    );
    indeNodes.forEach(x =>
      nodeIds.push(x.hasOwnProperty('topoNodeId') ? x.topoNodeId : x.indeNodeId)
    );

    const alreadyExists = nodeIds.some(element => element === id);

    if (alreadyExists) {
      setIsSubmitting(false);
      return entity === 'toponode'
        ? dispatch(duplicateTopoNode(id))
        : dispatch(duplicateIndeNode(id));
    }

    let passed = true;
    const requestBody = {};
    const parsedAsIntDerProgramListPollRateHours = parseInt(
      derProgramListPollRateHours
    );
    const parsedAsIntDerProgramListPollRateMinutes = parseInt(
      derProgramListPollRateMinutes
    );
    const parsedAsIntDerProgramListPollRateSeconds = parseInt(
      derProgramListPollRateSeconds
    );

    const convertedDerProgramListPollRate = convertToSeconds({
      hours: parsedAsIntDerProgramListPollRateHours,
      minutes: parsedAsIntDerProgramListPollRateMinutes,
      seconds: parsedAsIntDerProgramListPollRateSeconds
    });

    try {
      if (entity === RESOURCE_TYPES.TOPONODE) {
        if (parentTopoNodeId) {
          requestBody.parentTopoNodeId = parentTopoNodeId;
        } else if (childsParentTopoNodeId) {
          requestBody.parentTopoNodeId = childsParentTopoNodeId;
        }

        if (!/^[a-zA-Z0-9_.~-]*$/.test(id)) {
          passed = false;
          // TODO: Current error handling is here, will need to be revisited.
          setIdIsError(true);
        } else {
          requestBody.topoNodeId = id;
        }

        if (description.length > 32) {
          setDescriptionIsError(true);
          passed = false;
        } else {
          if (description !== '') {
            requestBody.description = description;
          }
          setDescriptionIsError(false);
        }

        //Optional pollRate
        if (
          derProgramListPollRateHours ||
          derProgramListPollRateMinutes ||
          derProgramListPollRateSeconds
        ) {
          if (
            isNaN(convertedDerProgramListPollRate) ||
            convertedDerProgramListPollRate < 0 ||
            convertedDerProgramListPollRate > 4294967295
          ) {
            setDerProgramListPollRateIsError(true);
            passed = false;
          } else {
            requestBody.pollRate = convertedDerProgramListPollRate;
          }
        } else {
          setDerProgramListPollRateIsError(false);
        }

        if (!passed) {
          return;
        }

        await dispatch(createToponode(requestBody));
        dispatch(
          setSidePanel({
            entity: RESOURCE_TYPES.TOPONODE,
            state: SIDEPANEL_STATES.READ,
            id
          })
        );
      } else if (entity === RESOURCE_TYPES.INDENODE) {
        const parsedAsIntPrimacy = parseInt(primacy);

        if (!id || !/^[a-zA-Z0-9_.~-]*$/.test(id) || id.length > 64) {
          setIdIsError(true);
          passed = false;
        } else {
          setIdIsError(false);
          requestBody.indeNodeId = id;
        }

        if (
          !/^\d+$/.test(primacy) ||
          isNaN(parsedAsIntPrimacy) ||
          parsedAsIntPrimacy > PRIMACY_MAX_VALUE ||
          parsedAsIntPrimacy < PRIMACY_MIN_VALUE
        ) {
          setPrimacyIsError(true);
          passed = false;
        } else {
          requestBody.primacy = parsedAsIntPrimacy;
          setPrimacyIsError(false);
        }

        //Optional description
        if (description.length > 32) {
          setDescriptionIsError(true);
          passed = false;
        } else {
          if (description !== '') {
            requestBody.description = description;
          }
          setDescriptionIsError(false);
        }

        //Optional pollRate
        if (
          derProgramListPollRateHours ||
          derProgramListPollRateMinutes ||
          derProgramListPollRateSeconds
        ) {
          if (
            isNaN(convertedDerProgramListPollRate) ||
            convertedDerProgramListPollRate < 0 ||
            convertedDerProgramListPollRate > 4294967295
          ) {
            setDerProgramListPollRateIsError(true);
            passed = false;
          } else {
            requestBody.pollRate = convertedDerProgramListPollRate;
          }
        } else {
          setDerProgramListPollRateIsError(false);
        }

        if (!passed) {
          return;
        }

        await dispatch(createIndeNode(requestBody));
        dispatch(
          setSidePanel({
            entity: RESOURCE_TYPES.INDENODE,
            state: SIDEPANEL_STATES.READ,
            id
          })
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const { entity } = sidePanel;

  if (!isActive || (isActive && isLoading)) {
    return null;
  } else {
    const topoNodeIds = topoNodes
      .filter(node => node.localNode !== LOCAL_RESOURCE_MAPPED_VALUES.REMOTE)
      .map(node => node.topoNodeId);
    const panelTitle = `New ${
      entity === RESOURCE_TYPES.TOPONODE ? `Topology` : `Independent`
    } Node`;
    const buttonText = `Create ${
      entity === RESOURCE_TYPES.TOPONODE ? `Topology` : `Independent`
    } Node`;
    let primacy = 0;
    if (childsParentTopoNodeId !== null) {
      primacy = topoNodes.find(
        topoNode => topoNode.topoNodeId === childsParentTopoNodeId
      ).primacy;
    }

    return (
      <StyledForm onSubmit={handleSubmit}>
        <FullPanel>
          <Panel primaryHeader title={panelTitle} />
          <PanelContent>
            {childsParentTopoNodeId !== null ? (
              <>
                <SubPanel>
                  <SubPanelRow>
                    <PanelRowDescriptor>Parent Node ID</PanelRowDescriptor>
                    <PanelRowDisplay>{childsParentTopoNodeId}</PanelRowDisplay>
                  </SubPanelRow>
                  <SubPanelRow>
                    <PanelRowDescriptor>Primacy</PanelRowDescriptor>
                    <PanelRowDisplay>{primacy}</PanelRowDisplay>
                  </SubPanelRow>
                </SubPanel>

                <Break />
              </>
            ) : null}

            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">ID</Text>
                </PanelRowDescriptor>
                <PanelRowTextForm
                  name="id"
                  onChange={handleInputChange}
                  isError={idIsError}
                  type="text"
                  minLength="1"
                  maxLength="64"
                  pattern="[a-zA-Z0-9_.~-]*$"
                  required
                />
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">Description</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <PanelRowTextForm
                  name="description"
                  onChange={handleInputChange}
                  isError={descriptionIsError}
                  type="text"
                  minLength="1"
                  maxLength="32"
                />
              </SubPanelRow>

              {entity === RESOURCE_TYPES.INDENODE ? (
                <SubPanelRow>
                  <PanelRowDescriptor>Primacy</PanelRowDescriptor>
                  <PanelRowTextForm
                    name="primacy"
                    type="number"
                    min={PRIMACY_MIN_VALUE}
                    max={PRIMACY_MAX_VALUE}
                    required
                    onChange={handleInputChange}
                    isError={primacyIsError}
                  />
                </SubPanelRow>
              ) : null}

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">New DER Program List Poll Rate</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>

                {/* hours */}
                <MultipleInputRowContainer>
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="derProgramListPollRateHours"
                      value={derProgramListPollRateHours}
                      onChange={handleInputChange}
                      min="0"
                      max="11929"
                      isError={derProgramListPollRateIsError}
                    />
                    <InputUnitLabel>hrs</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* minutes */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="derProgramListPollRateMinutes"
                      value={derProgramListPollRateMinutes}
                      onChange={handleInputChange}
                      min="0"
                      max="60"
                      isError={derProgramListPollRateIsError}
                    />
                    <InputUnitLabel>min</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* seconds */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="derProgramListPollRateSeconds"
                      value={derProgramListPollRateSeconds}
                      onChange={handleInputChange}
                      min="0"
                      max="60"
                      isError={derProgramListPollRateIsError}
                    />
                    <InputUnitLabel>sec</InputUnitLabel>
                  </InputAndLabelContainer>
                </MultipleInputRowContainer>
              </SubPanelRow>
            </SubPanel>

            {entity === RESOURCE_TYPES.TOPONODE &&
            childsParentTopoNodeId === null ? (
              <>
                <Break margin="8px 0" />

                <SubPanel>
                  <SubPanelRow>
                    <PanelRowDescriptor>
                      <Text fontSize="12px">Assign Parent Node</Text>
                      <Text fontSize="12px">(Optional)</Text>
                    </PanelRowDescriptor>

                    <PanelRowDisplay>
                      <SelectableSearchInput
                        isMulti={false}
                        options={topoNodeIds}
                        onChange={selected =>
                          handleSearchInputChange(selected, 'parentTopoNodeId')
                        }
                        selected={parentTopoNodeId}
                        name="parent-toponode-select"
                      />
                    </PanelRowDisplay>
                  </SubPanelRow>
                </SubPanel>
              </>
            ) : null}
          </PanelContent>
          <PanelButtonRow>
            <BlueBorderButton
              type="button"
              onClick={() =>
                determineBack(
                  sidePanel,
                  () => dispatch(revertSidePanel()),
                  () => dispatch(closeSidePanel())
                )
              }
            >
              Cancel
            </BlueBorderButton>
            <BlueButton type="submit" disabled={isSubmitting}>
              {buttonText}
            </BlueButton>
          </PanelButtonRow>
        </FullPanel>
      </StyledForm>
    );
  }
};

export default NewNode;
